import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  CalculatorIcon,
  XCircleIcon,
  InformationCircleIcon,
  CloudArrowUpIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

interface Transaction {
  id: string;
  type: string;
  amount: number;
  status: string;
  date: string;
  settlementDocument?: string;
  pnlCalculated?: boolean;
}

interface PNLData {
  settlementAmount: number;
  saleProceeds: number;
  tradingCosts: number;
  grossPNL: number;
  netPNL: number;
  pnlPercentage: string;
  profitStatus: 'profit' | 'loss';
}

interface TradeFileData {
  totalShares: number;
  totalProceeds: number;
  totalCosts: number;
  averagePrice: number;
  tradesProcessed: number;
}

const TradeManagement: React.FC = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [pnlData, setPnlData] = useState<PNLData | null>(null);
  const [tradeFileData, setTradeFileData] = useState<TradeFileData | null>(null);
  const [showPnlModal, setShowPnlModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      // Fetch transactions that need trade file uploads
      // For now, using mock data
      const mockTransactions: Transaction[] = [
        {
          id: '1',
          type: 'VWAP_PURCHASE',
          amount: 5000000,
          status: 'settled',
          date: '2024-04-15',
          settlementDocument: 'settlement_VWAP_1_20240415.docx',
          pnlCalculated: false
        },
        {
          id: '2',
          type: 'RAPID_PURCHASE',
          amount: 2000000,
          status: 'settled',
          date: '2024-04-10',
          settlementDocument: 'settlement_RAPID_2_20240410.docx',
          pnlCalculated: true
        },
        {
          id: '3',
          type: 'VWAP_PURCHASE',
          amount: 3000000,
          status: 'settled',
          date: '2024-04-05',
          settlementDocument: 'settlement_VWAP_3_20240405.docx',
          pnlCalculated: false
        }
      ];
      setTransactions(mockTransactions);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError('Failed to fetch transactions');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedTransaction) return;

    setUploadingFile(true);
    setUploadProgress(0);
    setError(null);

    const formData = new FormData();
    formData.append('tradeFile', file);
    formData.append('transactionId', selectedTransaction.id);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/settlement/trade-file/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1)
            );
            setUploadProgress(percentCompleted);
          },
        }
      );

      if (response.data.success) {
        setTradeFileData(response.data.data);
        // Fetch P&L data after successful upload
        await fetchPNLData(selectedTransaction.id);
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      setError(error.response?.data?.error || 'Failed to upload trade file');
    } finally {
      setUploadingFile(false);
    }
  };

  const fetchPNLData = async (transactionId: string) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/settlement/pnl/${transactionId}`
      );

      if (response.data.success) {
        setPnlData(response.data.data);
        setShowPnlModal(true);
        // Update transaction status
        setTransactions(prev =>
          prev.map(tx =>
            tx.id === transactionId ? { ...tx, pnlCalculated: true } : tx
          )
        );
      }
    } catch (error: any) {
      console.error('P&L fetch error:', error);
      setError(error.response?.data?.error || 'Failed to calculate P&L');
    }
  };

  const handleGenerateSettlement = async (transactionId: string) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/settlement/generate/${transactionId}`
      );

      if (response.data.success) {
        // Download the generated settlement statement
        window.open(response.data.data.downloadUrl, '_blank');
      }
    } catch (error: any) {
      console.error('Settlement generation error:', error);
      setError(error.response?.data?.error || 'Failed to generate settlement');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const getStatusBadge = (status: string, pnlCalculated?: boolean) => {
    if (status === 'settled' && pnlCalculated) {
      return 'bg-green-100 text-green-800 border-green-200';
    } else if (status === 'settled') {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    } else {
      return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Trade Management</h1>
                <p className="text-gray-600 mt-1">Upload trade files and calculate P&L</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Alert */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center gap-3"
          >
            <XCircleIcon className="h-5 w-5" />
            <p>{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-500 hover:text-red-600"
            >
              <XCircleIcon className="h-5 w-5" />
            </button>
          </motion.div>
        )}

        {/* Instructions Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6"
        >
          <div className="flex items-start gap-3">
            <InformationCircleIcon className="h-6 w-6 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">How to Upload Trade Files</h3>
              <ol className="list-decimal list-inside text-sm text-blue-700 space-y-1">
                <li>Select a settled transaction from the list below</li>
                <li>Click the "Upload Trade File" button</li>
                <li>Choose your CSV or Excel file containing trade execution data</li>
                <li>The system will automatically calculate P&L after upload</li>
              </ol>
              <p className="text-sm text-blue-600 mt-2">
                Trade files should include: Quantity, Price, and Commission columns
              </p>
            </div>
          </div>
        </motion.div>

        {/* Transactions Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200"
        >
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Settled Transactions</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transaction
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Settlement
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {transaction.type.replace('_', ' ')}
                        </p>
                        <p className="text-xs text-gray-500">ID: {transaction.id}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm font-semibold text-gray-900">
                        {formatCurrency(transaction.amount)}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(transaction.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(transaction.status, transaction.pnlCalculated)}`}>
                        {transaction.pnlCalculated ? 'P&L Calculated' : 'Awaiting Trade File'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {transaction.settlementDocument ? (
                        <button
                          onClick={() => window.open(`/api/settlement/download/${transaction.settlementDocument}`, '_blank')}
                          className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm"
                        >
                          <DocumentTextIcon className="h-4 w-4" />
                          Download
                        </button>
                      ) : (
                        <button
                          onClick={() => handleGenerateSettlement(transaction.id)}
                          className="text-gray-600 hover:text-gray-700 flex items-center gap-1 text-sm"
                        >
                          <DocumentTextIcon className="h-4 w-4" />
                          Generate
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        {!transaction.pnlCalculated && (
                          <>
                            <input
                              type="file"
                              id={`file-upload-${transaction.id}`}
                              accept=".csv,.xlsx,.xls"
                              onChange={handleFileUpload}
                              className="hidden"
                            />
                            <label
                              htmlFor={`file-upload-${transaction.id}`}
                              onClick={() => setSelectedTransaction(transaction)}
                              className="cursor-pointer px-3 py-1 bg-navy-700 text-white text-sm rounded-lg hover:bg-navy-800 flex items-center gap-2"
                            >
                              <CloudArrowUpIcon className="h-4 w-4" />
                              Upload Trade File
                            </label>
                          </>
                        )}
                        {transaction.pnlCalculated && (
                          <button
                            onClick={() => fetchPNLData(transaction.id)}
                            className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center gap-2"
                          >
                            <CalculatorIcon className="h-4 w-4" />
                            View P&L
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Upload Progress */}
        {uploadingFile && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-80"
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-gray-900">Uploading Trade File...</h4>
              <span className="text-sm text-gray-500">{uploadProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </motion.div>
        )}
      </div>

      {/* P&L Modal */}
      {showPnlModal && pnlData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">P&L Analysis</h2>
                <button
                  onClick={() => {
                    setShowPnlModal(false);
                    setPnlData(null);
                    setTradeFileData(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* P&L Summary */}
              <div className={`rounded-lg p-6 mb-6 ${
                pnlData.profitStatus === 'profit' 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Net P&L</h3>
                  <div className={`flex items-center gap-2 ${
                    pnlData.profitStatus === 'profit' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {pnlData.profitStatus === 'profit' ? (
                      <ArrowUpIcon className="h-5 w-5" />
                    ) : (
                      <ArrowDownIcon className="h-5 w-5" />
                    )}
                    <span className="text-2xl font-bold">
                      {formatCurrency(Math.abs(pnlData.netPNL))}
                    </span>
                    <span className="text-lg">
                      ({pnlData.pnlPercentage}%)
                    </span>
                  </div>
                </div>
              </div>

              {/* Detailed Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Settlement Details</h4>
                  <dl className="space-y-2">
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-500">Settlement Amount</dt>
                      <dd className="text-sm font-medium text-gray-900">
                        {formatCurrency(pnlData.settlementAmount)}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-500">Sale Proceeds</dt>
                      <dd className="text-sm font-medium text-gray-900">
                        {formatCurrency(pnlData.saleProceeds)}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-500">Trading Costs</dt>
                      <dd className="text-sm font-medium text-gray-900">
                        {formatCurrency(pnlData.tradingCosts)}
                      </dd>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-gray-200">
                      <dt className="text-sm font-medium text-gray-700">Gross P&L</dt>
                      <dd className={`text-sm font-bold ${
                        pnlData.grossPNL >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(pnlData.grossPNL)}
                      </dd>
                    </div>
                  </dl>
                </div>

                {tradeFileData && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Trade Execution Summary</h4>
                    <dl className="space-y-2">
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-500">Total Shares</dt>
                        <dd className="text-sm font-medium text-gray-900">
                          {formatNumber(tradeFileData.totalShares)}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-500">Average Price</dt>
                        <dd className="text-sm font-medium text-gray-900">
                          ${tradeFileData.averagePrice.toFixed(2)}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-500">Trades Processed</dt>
                        <dd className="text-sm font-medium text-gray-900">
                          {tradeFileData.tradesProcessed}
                        </dd>
                      </div>
                    </dl>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    // Export P&L report functionality
                    console.log('Export P&L report');
                  }}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                  Export Report
                </button>
                <button
                  onClick={() => {
                    setShowPnlModal(false);
                    setPnlData(null);
                    setTradeFileData(null);
                  }}
                  className="px-4 py-2 bg-navy-700 text-white rounded-lg hover:bg-navy-800"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default TradeManagement; 